import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Card, ExitPage } from "./ExitParts";
import { STAGE_SHORT, downloadCsv, exitApi, parseCsv } from "./exitApi";

const APPROVER_STAGES = ["SDM", "ADMIN", "IT", "FIN", "HR", "PAYROLL"];

export default function ExitMasters() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [orgs, setOrgs] = useState([]);
  const [org, setOrg] = useState("");
  const [stages, setStages] = useState([]);
  const [savedStages, setSavedStages] = useState([]);
  const [options, setOptions] = useState({});
  const [list, setList] = useState("exitType");
  const [approvers, setApprovers] = useState([]);
  const [newApprover, setNewApprover] = useState({ stage_key: "HR", user: null });
  const [userSearch, setUserSearch] = useState("");
  const [userResults, setUserResults] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    exitApi.organizations().then((r) => {
      setOrgs(r.data || []);
      if (r.data?.length) setOrg(r.data[0].id);
    }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!org) return;
    try {
      const [s, o, a] = await Promise.all([exitApi.stages(org), exitApi.options(org), exitApi.approvers(org)]);
      setStages(s.data || []);
      setSavedStages(s.data || []);
      setOptions(o.data || {});
      setApprovers(a.data || []);
    } catch { /* toast shown */ }
  }, [org]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (userSearch.trim().length < 2) { setUserResults([]); return; }
    const t = setTimeout(() => exitApi.searchUsers(userSearch).then((r) => setUserResults(r.data || [])).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [userSearch]);

  const edit = (key, patch) => setStages((ss) => ss.map((s) => (s.stage_key === key ? { ...s, ...patch } : s)));
  const dirty = JSON.stringify(stages) !== JSON.stringify(savedStages);

  const saveStages = async () => {
    setBusy(true);
    try {
      const payload = stages.map(({ stage_key, label, group, sla_hours, enabled }) => ({ stage_key, label, group: Number(group), sla_hours: Number(sla_hours), enabled }));
      const r = await exitApi.saveStages(org, payload);
      setStages(r.data); setSavedStages(r.data);
      toast.success("Stages saved. Open exits have been re-routed.");
    } catch { /* toast shown */ } finally { setBusy(false); }
  };

  const uploadOptions = async (file) => {
    const rows = parseCsv(await file.text()).filter((r) => r.list && r.value);
    if (!rows.length) { toast.error("The file needs two columns: list,value."); return; }
    try {
      const r = await exitApi.saveOptions(org, rows);
      setOptions(r.data);
      toast.success(r.message);
    } catch { /* toast shown */ }
  };

  const addApprover = async () => {
    try {
      const r = await exitApi.addApprover({ organization_id: org, stage_key: newApprover.stage_key, user_id: newApprover.user.id });
      toast.success(r.message);
      setNewApprover({ ...newApprover, user: null }); setUserSearch("");
      setApprovers((await exitApi.approvers(org)).data || []);
    } catch { /* toast shown */ }
  };

  const removeApprover = async (id) => {
    try {
      await exitApi.removeApprover(id);
      setApprovers((a) => a.filter((x) => x.id !== id));
      toast.success("Approver removed.");
    } catch { /* toast shown */ }
  };

  if (!orgs.length) {
    return (
      <ExitPage title="Exit Masters">
        <Card title="No access"><p className="exitwf-hint">Exit masters can be changed by HR approvers and super admins of an organization where the exit workflow is set up.</p></Card>
      </ExitPage>
    );
  }

  return (
    <ExitPage
      title="Exit Masters"
      actions={
        <>
          {orgs.length > 1 && (
            <select className="form-select exitwf-org" aria-label="Organization" value={org} onChange={(e) => setOrg(e.target.value)}>
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          )}
          <button className="exitwf-btn ghost" onClick={() => navigate("/exit-clearance")}>Back to list</button>
        </>
      }
    >
      <Card title="Workflow Stages & SLA">
        <p className="exitwf-hint">Stages with the same group run in parallel. Disabled stages are skipped. Saving re-routes exits that are in progress.</p>
        <div className="exitwf-table-wrap">
          <table className="exitwf-table plain">
            <thead><tr><th>Stage</th><th>Label</th><th>Group</th><th>SLA (hours)</th><th>Enabled</th></tr></thead>
            <tbody>
              {stages.map((s) => (
                <tr key={s.stage_key}>
                  <td>{STAGE_SHORT[s.stage_key]}</td>
                  <td><input className="form-control" aria-label={`${s.stage_key} label`} value={s.label} onChange={(e) => edit(s.stage_key, { label: e.target.value })} /></td>
                  <td><input className="form-control num" type="number" min="1" aria-label={`${s.stage_key} group`} value={s.group} disabled={s.stage_key === "EMP"} onChange={(e) => edit(s.stage_key, { group: e.target.value })} /></td>
                  <td><input className="form-control num" type="number" min="1" aria-label={`${s.stage_key} SLA hours`} value={s.sla_hours} onChange={(e) => edit(s.stage_key, { sla_hours: e.target.value })} /></td>
                  <td><input type="checkbox" aria-label={`${s.stage_key} enabled`} checked={s.enabled} disabled={s.stage_key === "EMP"} onChange={(e) => edit(s.stage_key, { enabled: e.target.checked })} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="exitwf-actions">
          <button className="exitwf-btn ghost" disabled={!dirty || busy} onClick={() => setStages(savedStages)}>Discard</button>
          <button className="exitwf-btn" disabled={!dirty || busy} onClick={saveStages}>Save Stages</button>
        </div>
      </Card>

      <Card title="Approvers">
        <p className="exitwf-hint">Employee and Reporting Manager are picked up automatically. Add who clears each of the other stages.</p>
        <div className="exitwf-toolbar">
          <select className="form-select exitwf-narrow" aria-label="Stage" value={newApprover.stage_key} onChange={(e) => setNewApprover({ ...newApprover, stage_key: e.target.value })}>
            {APPROVER_STAGES.map((k) => <option key={k} value={k}>{STAGE_SHORT[k]}</option>)}
          </select>
          <div className="exitwf-typeahead">
            <input className="form-control" aria-label="Find user" placeholder="Find user by name, email or code"
              value={newApprover.user ? `${newApprover.user.name} (${newApprover.user.email || newApprover.user.code})` : userSearch}
              onChange={(e) => { setNewApprover({ ...newApprover, user: null }); setUserSearch(e.target.value); }} />
            {!newApprover.user && userResults.length > 0 && (
              <ul>
                {userResults.map((u) => (
                  <li key={u.id}><button type="button" onClick={() => { setNewApprover({ ...newApprover, user: u }); setUserResults([]); }}>{u.name} <small>{u.email || u.code}</small></button></li>
                ))}
              </ul>
            )}
          </div>
          <button className="exitwf-btn" disabled={!newApprover.user} onClick={addApprover}>Add</button>
        </div>
        <div className="exitwf-table-wrap">
          <table className="exitwf-table plain">
            <thead><tr><th>Stage</th><th>Name</th><th>Email</th><th>Scope</th><th /></tr></thead>
            <tbody>
              {approvers.map((a) => (
                <tr key={a.id}>
                  <td>{STAGE_SHORT[a.stage_key]}</td><td>{a.user.name}</td><td>{a.user.email}</td>
                  <td>{a.client ? a.client.name : "All projects"}</td>
                  <td><button className="exitwf-btn ghost sm" onClick={() => removeApprover(a.id)}>Remove</button></td>
                </tr>
              ))}
              {!approvers.length && <tr><td colSpan={5} className="empty">No approvers yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="Dropdown Values">
        <div className="exitwf-toolbar">
          <select className="form-select exitwf-narrow" aria-label="Dropdown list" value={list} onChange={(e) => setList(e.target.value)}>
            {Object.keys(options).map((k) => <option key={k} value={k}>{k} ({options[k].length})</option>)}
          </select>
          <span className="spacer" />
          <button className="exitwf-btn ghost" onClick={() => downloadCsv("exit-dropdowns.csv",
            Object.entries(options).flatMap(([l, vs]) => vs.map((value) => ({ list: l, value }))),
            [{ label: "list", get: (r) => r.list }, { label: "value", get: (r) => r.value }])}>Download CSV</button>
          <button className="exitwf-btn ghost" onClick={() => fileRef.current.click()}>Upload CSV</button>
          <input ref={fileRef} type="file" accept=".csv,text/csv" hidden
            onChange={(e) => { const f = e.target.files[0]; if (f) uploadOptions(f); e.target.value = ""; }} />
        </div>
        <p className="exitwf-hint">CSV columns: <code>list,value</code>. Each list in the file replaces the current values of that list.</p>
        <div className="exitwf-chips">{(options[list] || []).map((v) => <span key={v} className="chip">{v}</span>)}</div>
      </Card>
    </ExitPage>
  );
}
